import type { BackgroundImage } from '../shared/types';

const PT_TO_MM = 25.4 / 72; // PDF points (1/72 inch) to millimetres

/**
 * Rasterise the first page of a PDF (base64 bytes) to a PNG data URL and return
 * it as a background underlay. The initial size is the PDF page size at 1:1
 * (so A4 ≈ 210×297 mm); the PDF-scale tool corrects it to the real scale.
 *
 * pdf.js is loaded as a native ESM module (see index.html) and exposed on
 * window.pdfjsLib, so it isn't bundled into the renderer.
 */
/** Rasterise one page of a PDF (base64) to a PNG data URL at the base resolution
 *  (crisp enough at fit; the visible slice is re-rasterised at higher zoom). Also
 *  returns the page's 1:1 physical size in mm. */
export async function rasterizePdfPage(base64: string, page = 1): Promise<{ dataUrl: string; paperWidthMm: number; paperHeightMm: number }> {
  const pdfjs = (window as unknown as { pdfjsLib?: any }).pdfjsLib;
  if (!pdfjs) throw new Error('PDF support is not available.');

  const data = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pg = await pdf.getPage(page);

  const base = pg.getViewport({ scale: 1 }); // page size in points
  // Render at a resolution that stays crisp when zoomed, capped for memory.
  const targetPx = 2200;
  const scale = Math.min(4, targetPx / Math.max(base.width, base.height) || 1);
  const viewport = pg.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(viewport.width));
  canvas.height = Math.max(1, Math.ceil(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create a canvas for the PDF.');
  await pg.render({ canvasContext: ctx, viewport }).promise;

  return { dataUrl: canvas.toDataURL('image/png'), paperWidthMm: base.width * PT_TO_MM, paperHeightMm: base.height * PT_TO_MM };
}

export async function renderPdfToBackground(base64: string): Promise<BackgroundImage> {
  const r = await rasterizePdfPage(base64, 1);
  return {
    dataUrl: r.dataUrl,
    x: 0,
    y: 0,
    widthMm: r.paperWidthMm,
    heightMm: r.paperHeightMm,
    paperWidthMm: r.paperWidthMm,
    paperHeightMm: r.paperHeightMm,
    // The base raster above is NOT persisted; only these PDF bytes are saved, and
    // the raster is regenerated from them on load (see drawingForSave / openProject).
    pdfData: base64,
    pdfPage: 1,
  };
}
